import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Pencil } from "lucide-react";

const FIELDS = [["line1", "Street address", "col-span-2"], ["line2", "Apt / unit (if any)", "col-span-2"], ["city", "City", ""], ["state", "State", ""], ["zip", "ZIP", ""]];
const fmt = (a) => a ? [a.line1, a.line2, a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ") : "";

export const MailingAddressReview = ({ prospectId, mailing, onUpdated }) => {
  const ok = ["verified", "corrected"].includes(mailing?.status);
  const [editing, setEditing] = useState(!ok);
  const [f, setF] = useState({ line1: "", line2: "", city: "", state: "", zip: "", ...(mailing?.address || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/prospects/${prospectId}/mailing-address`, f);
      if (["verified", "corrected"].includes(r.data.status)) { toast.success("Address verified"); setEditing(false); } else toast.error("Address still can't be verified");
      onUpdated?.(r.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className={`rounded-lg border p-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="mailing-review">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`flex items-center gap-1.5 font-semibold ${ok ? "text-emerald-800" : "text-red-700"}`} data-testid="mailing-status">
            {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {ok ? `Mailing address ${mailing.status} by USPS` : "Mailing address can't be verified — fix it before mailing"}
          </div>
          <div className="mt-1 text-slate-800" data-testid="mailing-address-final">{fmt(ok ? mailing.standardized : mailing?.address) || "No address on file"}</div>
          <div className="text-[11px] text-slate-500" data-testid="mailing-source">Source: {mailing?.address?.source || "—"}</div>
        </div>
        {!editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="mailing-edit-btn"><Pencil className="h-3.5 w-3.5" /></Button>}
      </div>
      {!ok && mailing?.errors?.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-red-700" data-testid="mailing-errors">
          {mailing.errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
      {editing && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {FIELDS.map(([k, label, span]) => (
            <label key={k} className={`text-[11px] font-semibold text-slate-600 ${span}`}>{label}
              <Input className="mt-0.5 h-8 bg-white" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`mailing-${k}`} />
            </label>
          ))}
          <Button onClick={save} disabled={busy} className="col-span-3 bg-slate-900 hover:bg-slate-800" data-testid="mailing-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & verify address"}
          </Button>
        </div>
      )}
    </div>
  );
};
